import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Landmark, Pencil, Trash2, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    listLiegenschaften, createLiegenschaft, updateLiegenschaft, deleteLiegenschaft,
    liegenschaftNamensvorschlag, liegenschaftVorschlag,
} from "@/lib/api";
import Dokumente from "@/components/Dokumente";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { Seitenkopf } from "@/hilfe/Seitenkopf";

// Die Beschriftungen stehen hier und nicht im Backend: dort ist der Zustand ein
// Schlüssel, hier ein Satz, den ein Mensch lesen soll.
const ZUSTAND_TEXT = {
    ungeteilt: "Ungeteilt — ein Objekt im Eigenbestand",
    geteilt_selbstverwaltet: "Geteilt — Verwaltung noch bei uns",
    weg_extern: "WEG — externe Hausverwaltung",
};
const ZUSTAND_KURZ = {
    ungeteilt: "ungeteilt",
    geteilt_selbstverwaltet: "geteilt, selbst verwaltet",
    weg_extern: "WEG extern",
};
const ZUSTAND_TON = {
    ungeteilt: "bg-gray-100 text-gray-700",
    geteilt_selbstverwaltet: "bg-amber-50 text-amber-800 border border-amber-200",
    weg_extern: "bg-sky-50 text-sky-800 border border-sky-200",
};

const leer = {
    bezeichnung: "", adressen: [], plz: "", stadt: "",
    zustand: "ungeteilt", verwalter_name: "", verwalter_seit: "",
    drive_ordner_id: "", notizen: "",
};

const LiegenschaftDialog = ({ offen, onOpenChange, start, onFertig }) => {
    const [form, setForm] = useState(leer);
    const [adressText, setAdressText] = useState("");
    const [speichert, setSpeichert] = useState(false);

    useEffect(() => {
        if (!offen) return;
        const f = { ...leer, ...(start || {}) };
        setForm(f);
        setAdressText((f.adressen || []).join("\n"));
    }, [offen, start]);

    const adressen = adressText.split("\n").map((a) => a.trim()).filter(Boolean);

    // Die Regel liegt im Backend. Sie hier nachzubauen hiesse, sie zweimal zu
    // haben -- und beim naechsten Sonderfall laufen die zwei auseinander.
    const vorschlagen = async () => {
        try {
            const r = await liegenschaftNamensvorschlag({ ...form, adressen });
            if (r.vorschlag) setForm((f) => ({ ...f, bezeichnung: r.vorschlag }));
            else toast.info("Erst eine Adresse eintragen");
        } catch { toast.error("Vorschlag nicht möglich"); }
    };

    const speichern = async () => {
        setSpeichert(true);
        try {
            const payload = { ...form, adressen };
            if (start?.id) await updateLiegenschaft(start.id, payload);
            else await createLiegenschaft(payload);
            toast.success(start?.id ? "Gespeichert" : "Liegenschaft angelegt");
            onOpenChange(false);
            onFertig();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht gespeichert", {
                description: typeof d === "string" ? d : (d?.error || "Bitte Eingaben prüfen"),
            });
        } finally { setSpeichert(false); }
    };

    return (
        <Dialog open={offen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{start?.id ? "Liegenschaft bearbeiten" : "Liegenschaft anlegen"}</DialogTitle>
                    <DialogDescription>
                        Die Klammer über den Objekten: Teilungserklärung, Beschlüsse und
                        Verwaltervertrag gelten hier, nicht an der einzelnen Wohnung.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label>Adressen — eine je Zeile</Label>
                        <Textarea rows={2} value={adressText}
                                  onChange={(e) => setAdressText(e.target.value)}
                                  placeholder={"Sophienstr. 4\nSophienstr. 6"}
                                  data-testid="input-lg-adressen" />
                        <div className="text-[11px] text-gray-500 mt-1">
                            Mehrere Hausnummern können eine Liegenschaft sein.
                        </div>
                    </div>

                    <div>
                        <Label>Bezeichnung</Label>
                        <div className="flex gap-2">
                            <Input value={form.bezeichnung}
                                   onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
                                   placeholder="z.B. WEG Sophienstr. 4-6"
                                   data-testid="input-lg-bezeichnung" />
                            <Button type="button" variant="outline" onClick={vorschlagen}
                                    aria-label="Nach Regel vorschlagen" hilfe="aktion.liegenschaftNamensvorschlag" data-testid="btn-lg-vorschlag">
                                <Wand2 size={14} />
                            </Button>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1">
                            Regel: ungeteilt die Adresse, aufgeteilt mit „WEG" davor.
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        <div><Label>PLZ</Label>
                            <Input value={form.plz || ""} onChange={(e) => setForm({ ...form, plz: e.target.value })} /></div>
                        <div className="col-span-2"><Label>Stadt</Label>
                            <Input value={form.stadt || ""} onChange={(e) => setForm({ ...form, stadt: e.target.value })} /></div>
                    </div>

                    <div>
                        <Label>Zustand</Label>
                        <SuchAuswahl wert={form.zustand} onWaehlen={(v) => setForm({ ...form, zustand: v })}
                            optionen={[
                                ...Object.entries(ZUSTAND_TEXT).map(([k, t]) => ({ wert: k, text: t })),
                            ]}
                            testId="input-lg-zustand"
                        />
                    </div>

                    {form.zustand === "weg_extern" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Hausverwaltung</Label>
                                <Input value={form.verwalter_name || ""}
                                       onChange={(e) => setForm({ ...form, verwalter_name: e.target.value })}
                                       placeholder="Pflicht bei externer Verwaltung"
                                       data-testid="input-lg-verwalter" />
                            </div>
                            <div>
                                <Label>verwaltet seit</Label>
                                <Input type="date" value={form.verwalter_seit || ""}
                                       onChange={(e) => setForm({ ...form, verwalter_seit: e.target.value })} />
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>Drive-Ordner (ID, optional)</Label>
                        <Input value={form.drive_ordner_id || ""}
                               onChange={(e) => setForm({ ...form, drive_ordner_id: e.target.value })}
                               placeholder="Ordner-ID aus Google Drive" />
                    </div>

                    <div>
                        <Label>Notizen</Label>
                        <Textarea rows={2} value={form.notizen || ""}
                                  onChange={(e) => setForm({ ...form, notizen: e.target.value })} />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={speichern} disabled={speichert} grund="Wird gespeichert"
                            className="bg-slate-900 hover:bg-slate-800" data-testid="btn-lg-speichern">
                        Speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Liegenschaften = () => {
    const [items, setItems] = useState([]);
    const [laedt, setLaedt] = useState(true);
    const [offen, setOffen] = useState(false);
    const [start, setStart] = useState(null);
    // Welche Liegenschaft ihre Dokumente zeigt. Eine zur Zeit: zwei offene
    // Listen nebeneinander vergleicht niemand, sie verlaengern nur die Seite.
    const [offeneDok, setOffeneDok] = useState(null);

    const laden = useCallback(async () => {
        setLaedt(true);
        try { setItems(await listLiegenschaften()); }
        catch { toast.error("Liegenschaften nicht geladen"); }
        finally { setLaedt(false); }
    }, []);

    useEffect(() => { laden(); }, [laden]);

    const entfernen = async (lg) => {
        try {
            await deleteLiegenschaft(lg.id);
            toast.success("Gelöscht");
            laden();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.error || "Nicht gelöscht", { description: d?.hinweis });
        }
    };

    // Der Trockenlauf zeigt, was entstehen würde. Geschrieben wird erst auf
    // ausdrückliche Bestätigung -- hier entstehen Stammdaten, keine Ansicht.
    const migrieren = async () => {
        try {
            const probe = await liegenschaftVorschlag({ apply: false });
            if (!probe.liegenschaften_vorgeschlagen) {
                toast.info("Nichts zu tun — alle Objekte haben eine Liegenschaft");
                return;
            }
            const ok = window.confirm(
                `${probe.liegenschaften_vorgeschlagen} Liegenschaften aus `
                + `${probe.objekte_ohne_liegenschaft} Objekten anlegen?\n\n`
                + probe.entwurf.slice(0, 8).map((e) => `· ${e.bezeichnung} (${e.anzahl_objekte})`).join("\n")
                + (probe.entwurf.length > 8 ? `\n… und ${probe.entwurf.length - 8} weitere` : ""));
            if (!ok) return;
            const r = await liegenschaftVorschlag({ apply: true });
            toast.success(`${r.davon_angelegt} Liegenschaften angelegt`);
            laden();
        } catch { toast.error("Vorschlag fehlgeschlagen"); }
    };

    return (
        <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900">Liegenschaften</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Grundstück und Gebäude als Einheit — darüber hängen Teilungserklärung,
                        Beschlüsse und Verwaltervertrag. Mehrere Objekte derselben Liegenschaft
                        teilen sie sich, statt sie mehrfach zu führen.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button variant="outline" onClick={migrieren} data-testid="btn-lg-migration">
                        <Wand2 size={14} className="mr-1.5" /> Aus Objekten ableiten
                    </Button>
                    <Button onClick={() => { setStart(null); setOffen(true); }}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="btn-lg-neu">
                        <Plus size={14} className="mr-1.5" /> Neu
                    </Button>
                </div>
            </div>
            <Seitenkopf id="seite.liegenschaften" />

            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.14em] text-gray-500">
                        <tr>
                            <th className="px-4 py-2.5 text-left">Bezeichnung</th>
                            <th className="px-4 py-2.5 text-left">Adressen</th>
                            <th className="px-4 py-2.5 text-left">Zustand</th>
                            <th className="px-4 py-2.5 text-left">Verwaltung</th>
                            <th className="px-4 py-2.5 text-right">Objekte</th>
                            <th className="px-4 py-2.5 text-right">Einheiten</th>
                            <th className="px-4 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {items.map((lg) => (
                            <tr key={lg.id} className="hover:bg-gray-50/60" data-testid={`lg-${lg.id}`}>
                                <td className="px-4 py-2.5 font-medium text-slate-900">
                                    <button type="button"
                                            className="inline-flex items-center gap-2 hover:underline underline-offset-2"
                                            title="Dokumente dieser Liegenschaft"
                                            data-testid={`lg-dokumente-${lg.id}`}
                                            onClick={() => setOffeneDok(offeneDok === lg.id ? null : lg.id)}>
                                        <Landmark size={13} className="text-gray-400" />
                                        {lg.bezeichnung}
                                    </button>
                                </td>
                                <td className="px-4 py-2.5 text-gray-600">
                                    {(lg.adressen || []).join(" · ") || "—"}
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className={`text-[11px] px-2 py-0.5 rounded ${ZUSTAND_TON[lg.zustand] || ""}`}>
                                        {ZUSTAND_KURZ[lg.zustand] || lg.zustand}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-gray-600">
                                    {lg.verwalter_name || "—"}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums">
                                    {lg.objekte > 0 ? (
                                        <Link to={`/immobilien?liegenschaft_id=${lg.id}`}
                                              className="hover:underline underline-offset-2">{lg.objekte}</Link>
                                    ) : "0"}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{lg.einheiten}</td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                    <Button variant="ghost" size="sm" className="h-8 px-2"
                                            onClick={() => { setStart(lg); setOffen(true); }}
                                            data-testid={`lg-bearbeiten-${lg.id}`}>
                                        <Pencil size={13} />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-rose-700"
                                            onClick={() => entfernen(lg)}
                                            data-testid={`lg-loeschen-${lg.id}`}>
                                        <Trash2 size={13} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {items.map((lg) => (offeneDok === lg.id ? (
                            <tr key={`${lg.id}-dok`} className="bg-gray-50/50">
                                <td colSpan={7} className="px-4 py-4">
                                    <Dokumente zielId={lg.id} zielName={lg.bezeichnung}
                                               drivOrdnerId={lg.drive_ordner_id} aufLiegenschaft />
                                </td>
                            </tr>
                        ) : null))}
                        {!items.length && (
                            <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-gray-400">
                                {laedt ? "Lädt …" : (
                                    <>Noch keine Liegenschaft. „Aus Objekten ableiten" schlägt
                                       welche aus den vorhandenen Adressen vor.</>
                                )}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <LiegenschaftDialog offen={offen} onOpenChange={setOffen} start={start} onFertig={laden} />
        </div>
    );
};

export default Liegenschaften;
